import { Injectable, Injector } from "@angular/core";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { WidgetsAbstract } from "src/app/abstract/widgets/widgets.abstract";
import { WidgetsTypeEnum } from "src/app/enums/widgets/widgets-type.enum";
import { WidgetsQuestionModel } from "src/app/models/widgets/widgets-question.model";
import { WidgetsFormModel } from "src/app/models/widgets/widgets-form.model";
import { Logger } from "src/app/core/logger";
import { RenderService } from "./render.service";
import { AbstractRender } from "src/app/abstract/render.abstract";
import { Config } from "src/app/config";
import { WidgetsFieldModel } from "src/app/models/widgets/widgets-field.model";

@Injectable({
  providedIn: 'root'
})
export class RenderQuestionService extends AbstractRender {
  constructor(
    private injector: Injector,
    private logger: Logger
  ) {
    super();
  }

  /**
   * Recursive render method
   *
   * @param widget A layout node
   * @param config Formly field config array
   * @param widgetRoot Root of the layout
   * @param formlyRoot Root of the FormlyFieldConfig
   * @param isInsideQuestion True if we are rendering a subtree inside a question
   * @param isRecursive True if we want to traverse the tree to the bottom. False if we just want to render a node.
   */
  render(widget: WidgetsAbstract,
    config: FormlyFieldConfig[],
    widgetRoot: WidgetsAbstract,
    formlyRoot: FormlyFieldConfig,
    isInsideQuestion: boolean,
    isRecursive: boolean) {
    let field: FormlyFieldConfig;

    try {
      if (widget instanceof WidgetsQuestionModel) {
        const questionWidget = widget as WidgetsQuestionModel;

        questionWidget.isComplex = false;
        let child = questionWidget.children[0] as WidgetsFieldModel

        if (questionWidget.children.length > 1) {
          questionWidget.isComplex = true;
        } else if (questionWidget.children.length === 1 && (child.type === 'radio' || child.type === 'checkbox') && child.commentFieldOn) {
          questionWidget.isComplex = true;
        } else if (questionWidget.children.length === 1 && child.type === 'input' && child.commentKey) {
          questionWidget.isComplex = true;
        } else if (questionWidget.isTable === true) {
          questionWidget.isComplex = true;
        }

        field = {
          wrappers: ['question'],
          props: {
            label: questionWidget.name,
            sequence: questionWidget.seq,
            disabled: false,
            id: questionWidget.id,
            dbid: questionWidget.dbId,
            isPresentInRiskForm: questionWidget.isPresentInRiskForm ? questionWidget.isPresentInRiskForm : false,
            riskFormDescription: questionWidget.riskFormDescription ? questionWidget.riskFormDescription : '',
            isTable: questionWidget.isTable ? questionWidget.isTable : false,
            isComplex: questionWidget.isComplex ? questionWidget.isComplex : false,
            isOnPoint: questionWidget.isOnPoint ? questionWidget.isOnPoint : false,
            attributes: {
              isCommentRequired: "" + questionWidget.isCommentRequired,
              commentTrigger: questionWidget.commentTrigger ? questionWidget.commentTrigger : '',
              smartFormDescription: questionWidget.smartFormDescription ? questionWidget.smartFormDescription : ''
            }
          },
          fieldGroup: []
        };

        if (questionWidget.key !== '') {
          field.key = this.isFlatModelSubmitEnabled() ? undefined : questionWidget.key;
          // eslint-disable-next-line
          field.props!.description = questionWidget.key;
        }

        // Initial Section Question implementation
        try {
          const sectionInitialFormlyFieldConfig = config as FormlyFieldConfig[];
          const sectionInitialFormlyFieldGroup = sectionInitialFormlyFieldConfig?.[0]?.fieldGroup as FormlyFieldConfig[] | undefined;
          if (sectionInitialFormlyFieldGroup?.[1]) {
            const sectionInitialQuesKey = sectionInitialFormlyFieldGroup[1].key;
            const isInitialSectionQuestion = sectionInitialFormlyFieldGroup[1].props?.isInitialSectionQuestion;

            if (isInitialSectionQuestion && sectionInitialQuesKey) {
              const hideQuestion = {
                expressions: {
                  // hide: `(field.parent.model.${sectionInitialQuesKey}No!=undefined) && field.parent.model.${sectionInitialQuesKey}No === true`
                  hide: `field.parent.model.${sectionInitialQuesKey} === 'No'`
                },
              };
              field = Object.assign({}, field, hideQuestion);
            }
          }
        } catch (error) {
          this.logger.log(error);
        }

        // Mix created input with widget template
        if (questionWidget.template) {
          const modelTemplate = JSON.parse(questionWidget.template);
          field = Object.assign({}, field, modelTemplate);
        }

        config.push(field);

        if (isRecursive) {
          const renderService = this.injector.get(RenderService);
          questionWidget.children.forEach(childWidget => {
            // eslint-disable-next-line
            renderService.render(childWidget, field.fieldGroup!, widget, formlyRoot, isInsideQuestion, isRecursive);
          });
        }
      }
    } catch (error) {
      this.logger.log(error);
    }
  }

  isFlatModelSubmitEnabled(): boolean {
    if (Config.FEATURES.get('isFlatModelSubmitEnabled')) {
      return Config.FEATURES.get('isFlatModelSubmitEnabled') as boolean;
    }
    return false;
  }
}
